import { useState } from 'react'
import { Plus, ShieldCheck, Trash2 } from 'lucide-react'
import { DataTable, type DataTableColumn } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { InfoTooltip } from '@/components/InfoTooltip'
import type { Firm, ProductTypesFile } from '@/types/bundle'
import { filterFieldsForFirm, readOverrideValue, type ConcreteOverridableField } from '../overrideUtils'
import { AddOverrideModal } from './AddOverrideModal'
import { OverrideValueInputCell } from './OverrideValueInputCell'

export interface OverridesSectionProps {
  firm: Firm
  /** Every allow-listed, concrete field for the whole draft (governance-
   * expanded once at the screen level) - filtered here by whether the
   * current firm has actually overridden it, and (for the Add Override
   * popup only) by whether it applies to this firm at all. */
  allFields: ConcreteOverridableField[]
  productTypes: ProductTypesFile
  onSetValue: (field: ConcreteOverridableField, value: number) => void
  onRemove: (field: ConcreteOverridableField) => void
  serverErrorFor: (field: ConcreteOverridableField) => string | undefined
}

// Only allow-listed fields ever appear here (PRD 06 AC2) - the picker/list
// is built from expandAllowlistToConcreteFields, never from whatever
// happens to already be in clients.json's overrides block.
export function OverridesSection({
  firm,
  allFields,
  productTypes,
  onSetValue,
  onRemove,
  serverErrorFor,
}: OverridesSectionProps) {
  const [addModalOpen, setAddModalOpen] = useState(false)

  const overriddenFields = allFields.filter((field) => readOverrideValue(firm, field) !== undefined)
  const unoverriddenFields = allFields.filter((field) => readOverrideValue(firm, field) === undefined)
  // Only scopes what's *offered to add* - an existing override (above) stays
  // visible/removable even if its product/document type no longer applies.
  const availableFields = filterFieldsForFirm(unoverriddenFields, firm, productTypes)

  const columns: DataTableColumn<ConcreteOverridableField>[] = [
    { key: 'field', header: 'Field', render: (field) => field.fieldLabel },
    { key: 'appliesTo', header: 'Applies to', render: (field) => field.entityLabel },
    {
      key: 'default',
      header: 'Platform default',
      render: (field) => <span className="text-text-disabled">{String(field.platformDefaultValue ?? '-')}</span>,
    },
    {
      key: 'value',
      header: 'Override value',
      render: (field) => (
        <OverrideValueInputCell
          field={field}
          value={readOverrideValue(firm, field)}
          onChange={(value) => onSetValue(field, value)}
          serverError={serverErrorFor(field)}
        />
      ),
    },
    {
      key: 'remove',
      header: '',
      align: 'right',
      render: (field) => (
        <button
          type="button"
          aria-label={`Remove override for ${field.entityLabel}: ${field.fieldLabel}`}
          onClick={() => onRemove(field)}
          className="text-text-secondary hover:text-danger"
        >
          <Trash2 size={16} strokeWidth={1.5} />
        </button>
      ),
    },
  ]

  return (
    <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
          Overrides
          <InfoTooltip text="Values set for this firm only, replacing the platform default. Only fields the platform allows to be overridden can appear here." />
        </h2>
        <button
          type="button"
          onClick={() => setAddModalOpen(true)}
          className="flex items-center gap-1 rounded-control border border-border px-3 py-1.5 text-meta font-medium text-text-primary hover:border-accent/60"
        >
          <Plus size={14} strokeWidth={1.5} />
          Add Override
        </button>
      </div>

      {overriddenFields.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          headline="This firm uses all platform defaults"
          description="Add an override to tighten a limit or threshold for this firm only."
          action={{ label: '+ Add Override', onClick: () => setAddModalOpen(true) }}
        />
      ) : (
        <DataTable columns={columns} data={overriddenFields} getRowKey={(field) => field.canonicalPath} />
      )}

      <AddOverrideModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        availableFields={availableFields}
        onSelect={(field) =>
          onSetValue(field, typeof field.platformDefaultValue === 'number' ? field.platformDefaultValue : 0)
        }
      />
    </section>
  )
}
